/**
 * Marker derivation for the Admin Live Tracking Map (ODE-P07-T02).
 *
 * Pure functions over the P07-T01 overview snapshot (consumed unchanged):
 *
 *   - `deriveMapMarkers` — one row per server-derived live position
 *     (REQ-043), plotted at the LAST KNOWN FIX coordinates exactly as the
 *     server reported them (never extrapolated, interpolated or fabricated).
 *     The per-marker `stale` flag applies the SAME comparison as the
 *     overview read model: `freshnessMs > staleAfterMs` (server-published
 *     bound, REQ-090).
 *   - `toMapMarkers` — projection of the display rows onto the adapter
 *     contract (MapProvider port).
 *   - `deriveUnfixed` — active sessions/DOs WITHOUT any fix are listed,
 *     never plotted: DO-level rows come from the checked-in DO facts
 *     (`hasActiveSession` true with no plotted position row), and the
 *     session count is the server KPI difference (`activeSessions -
 *     plotted`). Both are pure projections of server data — nothing
 *     invented.
 *   - small deterministic formatters for the fix timestamp / age text
 *     (status is never encoded by color alone — REQ-090).
 *
 * Traces to: ODE-PRD-REQ-043, ODE-PRD-REQ-084, ODE-PRD-REQ-090.
 */

import type { OverviewSnapshot } from '../../api/client.js';
import type { MapMarkerView } from './map-provider.js';

/** One display row for a plotted marker (server fix fields, verbatim). */
export interface MapMarkerRow {
  readonly trackingSessionId: string;
  readonly driverId: string;
  readonly deliveryOrderId: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly accuracyMeters: number | null;
  readonly capturedAtEpochMs: number;
  readonly freshnessMs: number;
  readonly stale: boolean;
}

/** One unfixed active DO row (listed, never plotted). */
export interface UnfixedDeliveryOrderRow {
  readonly deliveryOrderId: string;
  readonly lastActivityAtEpochMs: number | null;
}

/** Derivation result for the map body. */
export interface MapDerivation {
  readonly rows: readonly MapMarkerRow[];
  /** Positions at or below the server stale bound (fresh). */
  readonly freshCount: number;
  /** Positions beyond the server stale bound (stale; still last-known). */
  readonly staleCount: number;
  /** Active sessions the server counts that have no fix to plot. */
  readonly unfixedSessionCount: number;
  /** DO-level rows with an active session but no plotted position. */
  readonly unfixedDeliveryOrders: readonly UnfixedDeliveryOrderRow[];
}

/** Formats an epoch-ms instant as a deterministic ISO string. */
export function formatFixTime(epochMs: number): string {
  return new Date(epochMs).toISOString();
}

/** Plain-text age label (status is never color-only). */
export function formatAgeMs(freshnessMs: number): string {
  const seconds = Math.max(0, Math.round(freshnessMs / 1000));
  return `${seconds} dtk lalu`;
}

/**
 * Derives marker display rows + freshness counts from the server snapshot.
 * Each row carries the exact server coordinates; the stale flag is
 * `freshnessMs > staleAfterMs` (the same bound the overview read model
 * publishes — a fix exactly at the bound is still fresh, matching the
 * read-model rule).
 */
export function deriveMapDerivation(snapshot: OverviewSnapshot): MapDerivation {
  const rows: MapMarkerRow[] = snapshot.livePositions.map((position) => ({
    trackingSessionId: position.trackingSessionId,
    driverId: position.driverId,
    deliveryOrderId: position.deliveryOrderId,
    latitude: position.latitude,
    longitude: position.longitude,
    accuracyMeters: position.accuracyMeters,
    capturedAtEpochMs: position.capturedAtEpochMs,
    freshnessMs: position.freshnessMs,
    stale: position.freshnessMs > snapshot.staleAfterMs,
  }));

  const plottedDeliveryOrders = new Set(rows.map((row) => row.deliveryOrderId));
  const unfixedDeliveryOrders: UnfixedDeliveryOrderRow[] = snapshot.doStatus
    .filter((row) => row.hasActiveSession && !plottedDeliveryOrders.has(row.deliveryOrderId))
    .map((row) => ({
      deliveryOrderId: row.deliveryOrderId,
      lastActivityAtEpochMs: row.lastActivityAtEpochMs,
    }));

  const unfixedSessionCount = Math.max(0, snapshot.kpi.activeSessions - rows.length);

  return {
    rows,
    freshCount: rows.filter((row) => !row.stale).length,
    staleCount: rows.filter((row) => row.stale).length,
    unfixedSessionCount,
    unfixedDeliveryOrders,
  };
}

/** Projects display rows onto the MapProvider adapter contract. */
export function toMapMarkers(rows: readonly MapMarkerRow[]): readonly MapMarkerView[] {
  return rows.map((row) => ({
    id: row.trackingSessionId,
    latitude: row.latitude,
    longitude: row.longitude,
    label: `${row.driverId} · ${row.deliveryOrderId}`,
    stale: row.stale,
  }));
}

/** Formats an accuracy value for display (em dash when absent). */
export function formatAccuracy(accuracyMeters: number | null): string {
  return accuracyMeters === null ? '—' : `${accuracyMeters} m`;
}
